import { useState, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Loader2, Search, UserPlus, Trash2, ChevronDown, User } from 'lucide-react';
import {
  useWatchlistCollaboratorsQuery,
  useAddCollaboratorMutation,
  useUpdateCollaboratorRoleMutation,
  useRemoveCollaboratorMutation,
  useUserSearchQuery,
  type Collaborator,
  type CollaboratorRole,
  type UserSearchResult,
} from '../hooks/useListsQuery';
import { useAuth } from '../contexts/AuthContext';
import { effectiveTier } from '../utils/entitlements';
import { UpgradeCTA } from './ui/UpgradeCTA';
import { ApiError } from '../lib/api';

interface ManageCollaboratorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  listId: string;
  listName: string;
  isOwner: boolean;
}

// Debounce hook for search input (uses useEffect for proper cleanup)
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timeout);
  }, [value, delay]);

  return debouncedValue;
}

function Avatar({ url, name, size = 40 }: { url?: string | null; name?: string; size?: number }) {
  if (url) {
    return (
      <img
        src={url}
        alt={name || 'User'}
        className="rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <div
      className="rounded-full bg-[#1A1F26] flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <User size={size * 0.5} className="text-slate-500" />
    </div>
  );
}

interface CollaboratorRowProps {
  collaborator: Collaborator;
  listId: string;
  isOwner: boolean;
  isCurrentUser: boolean;
  onRoleChange: (userId: string, role: CollaboratorRole) => void;
  onRemove: (userId: string) => void;
  onLeave?: () => void;
  isUpdating: boolean;
  isRemoving: boolean;
}

function CollaboratorRow({
  collaborator,
  isOwner,
  isCurrentUser,
  onRoleChange,
  onRemove,
  onLeave,
  isUpdating,
  isRemoving,
}: CollaboratorRowProps) {
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);

  const displayName = collaborator.display_name || collaborator.username || 'User unavailable';

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-[#1A1F26]/50">
      <Avatar url={collaborator.avatar_url} name={displayName} size={40} />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-white truncate">{displayName}</p>
        {collaborator.username && (
          <p className="text-sm text-slate-500">@{collaborator.username}</p>
        )}
      </div>

      {/* Role Dropdown (owner) or Role Badge (non-owner) */}
      {isOwner ? (
        <div className="relative">
          <button
            onClick={() => setShowRoleDropdown(!showRoleDropdown)}
            disabled={isUpdating}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-[#252B33] ring-1 ring-white/[0.08] rounded-lg text-slate-300 hover:bg-[#2A3038] transition-colors disabled:opacity-50"
          >
            {isUpdating ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <>
                {collaborator.role === 'editor' ? 'Editor' : 'Viewer'}
                <ChevronDown size={14} />
              </>
            )}
          </button>

          {showRoleDropdown && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowRoleDropdown(false)}
              />
              <div className="absolute right-0 top-full mt-1 z-20 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg shadow-xl overflow-hidden min-w-[120px]">
                <button
                  onClick={() => {
                    onRoleChange(collaborator.user_id, 'editor');
                    setShowRoleDropdown(false);
                  }}
                  className={`w-full px-3 py-2 text-sm text-left hover:bg-[#252B33] transition-colors ${
                    collaborator.role === 'editor' ? 'text-teal-400' : 'text-slate-300'
                  }`}
                >
                  Editor
                  <p className="text-xs text-slate-500">Can add & edit items</p>
                </button>
                <button
                  onClick={() => {
                    onRoleChange(collaborator.user_id, 'viewer');
                    setShowRoleDropdown(false);
                  }}
                  className={`w-full px-3 py-2 text-sm text-left hover:bg-[#252B33] transition-colors ${
                    collaborator.role === 'viewer' ? 'text-teal-400' : 'text-slate-300'
                  }`}
                >
                  Viewer
                  <p className="text-xs text-slate-500">Can only view</p>
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <span className="text-xs text-slate-400 capitalize">{collaborator.role}</span>
      )}

      {/* Remove Button (owner) or Leave Button (non-owner, current user) */}
      {isOwner ? (
        <button
          onClick={() => onRemove(collaborator.user_id)}
          disabled={isRemoving}
          className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
          title="Remove collaborator"
        >
          {isRemoving ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
        </button>
      ) : isCurrentUser && onLeave ? (
        <button
          onClick={onLeave}
          className="text-sm text-red-400 hover:text-red-300 px-3 py-1 rounded-lg hover:bg-red-500/10 transition-colors"
        >
          Leave
        </button>
      ) : null}
    </div>
  );
}

interface UserSearchResultRowProps {
  user: UserSearchResult;
  onAdd: (user: UserSearchResult) => void;
  isAdding: boolean;
  isAlreadyCollaborator: boolean;
}

function UserSearchResultRow({ user, onAdd, isAdding, isAlreadyCollaborator }: UserSearchResultRowProps) {
  const displayName = user.display_name || user.username;

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#1A1F26]/50 transition-colors">
      <Avatar url={user.avatar_url} name={displayName} size={36} />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-white truncate">{displayName}</p>
        <p className="text-sm text-slate-500">@{user.username}</p>
      </div>

      {isAlreadyCollaborator ? (
        <span className="text-sm text-slate-500">Already added</span>
      ) : (
        <button
          onClick={() => onAdd(user)}
          disabled={isAdding}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-teal-500 text-white rounded-lg hover:bg-teal-400 transition-colors disabled:opacity-50"
        >
          {isAdding ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <>
              <UserPlus size={14} />
              Add
            </>
          )}
        </button>
      )}
    </div>
  );
}

export function ManageCollaboratorsModal({
  isOpen,
  onClose,
  listId,
  listName,
  isOwner,
}: ManageCollaboratorsModalProps) {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const isFree = !!profile && effectiveTier(profile) === 'free';
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<CollaboratorRole>('viewer');
  const [error, setError] = useState<string | null>(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // Debounce search query
  const debouncedQuery = useDebouncedValue(searchQuery, 300);

  // Queries & Mutations
  const { collaborators, isLoading: isLoadingCollaborators } = useWatchlistCollaboratorsQuery(listId, {
    enabled: isOpen,
  });
  const { users: searchResults, isLoading: isSearching } = useUserSearchQuery(debouncedQuery, {
    enabled: isOpen && debouncedQuery.length >= 2,
  });
  const addMutation = useAddCollaboratorMutation();
  const updateRoleMutation = useUpdateCollaboratorRoleMutation();
  const removeMutation = useRemoveCollaboratorMutation();

  // Filter out the owner and existing collaborators from search results
  const filteredSearchResults = useMemo(() => {
    const collaboratorIds = new Set(collaborators.map((c) => c.user_id));
    return searchResults.filter(
      (u) => u.id !== user?.id && !collaboratorIds.has(u.id)
    );
  }, [searchResults, collaborators, user?.id]);

  const handleAddCollaborator = useCallback(async (userToAdd: UserSearchResult) => {
    try {
      setError(null);
      await addMutation.mutateAsync({
        listId,
        userId: userToAdd.id,
        role: selectedRole,
      });
      setSearchQuery('');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'SOCIAL_TIER_REQUIRED') {
        setError('Upgrade to Social to manage collaborators');
      } else if (err instanceof ApiError && err.code === 'TARGET_USER_FREE_TIER') {
        setError('This user needs a Social subscription to be added as a collaborator');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to add collaborator');
      }
    }
  }, [addMutation, listId, selectedRole]);

  const handleRoleChange = useCallback(async (userId: string, role: CollaboratorRole) => {
    try {
      setError(null);
      await updateRoleMutation.mutateAsync({ listId, userId, role });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'SOCIAL_TIER_REQUIRED') {
        setError('Upgrade to Social to manage collaborators');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to update role');
      }
    }
  }, [updateRoleMutation, listId]);

  const handleRemove = useCallback(async (userId: string) => {
    try {
      setError(null);
      await removeMutation.mutateAsync({ listId, userId });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'SOCIAL_TIER_REQUIRED') {
        setError('Upgrade to Social to manage collaborators');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to remove collaborator');
      }
    }
  }, [removeMutation, listId]);

  const handleLeaveList = () => {
    if (!user) return;
    removeMutation.mutate(
      { listId, userId: user.id },
      {
        onSuccess: () => {
          onClose();
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            navigate('/lists');
          }
        },
      }
    );
  };

  const handleClose = () => {
    setSearchQuery('');
    setError(null);
    setShowLeaveConfirm(false);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#0A0A0A]/80 backdrop-blur-sm"
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          handleClose();
        }}
      />

      {/* Modal */}
      <div
        className="relative bg-[#0F1419] rounded-xl ring-1 ring-white/[0.06] w-full max-w-2xl shadow-2xl shadow-black/50 h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
          <div>
            <h2 className="text-lg font-semibold text-white">{isOwner ? 'Manage Collaborators' : 'Collaborators'}</h2>
            <p className="text-sm text-slate-500">{listName}</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-[#1A1F26] rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {isFree && isOwner ? (
          /* Upgrade CTA for Free owner users */
          <div className="flex-1 flex items-center justify-center">
            <UpgradeCTA feature="Managing collaborators" onNavigate={handleClose} />
          </div>
        ) : (
        <>
        {/* Search Section (owner only) */}
        {isOwner && (
        <div className="p-4 border-b border-white/[0.06]">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users by name..."
                className="w-full pl-10 pr-4 py-2 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value as CollaboratorRole)}
              className="px-3 py-2 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
            </select>
          </div>

          {/* Search Results */}
          {searchQuery.length >= 2 && (
            <div className="mt-3 max-h-72 overflow-y-auto">
              {isSearching ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 size={20} className="animate-spin text-slate-400" />
                </div>
              ) : filteredSearchResults.length === 0 ? (
                <p className="text-center text-sm text-slate-500 py-4">
                  {searchResults.length === 0 ? 'No users found' : 'All matching users are already collaborators'}
                </p>
              ) : (
                <div className="space-y-1">
                  {filteredSearchResults.map((u) => (
                    <UserSearchResultRow
                      key={u.id}
                      user={u}
                      onAdd={handleAddCollaborator}
                      isAdding={addMutation.isPending && addMutation.variables?.userId === u.id}
                      isAlreadyCollaborator={false}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {/* Error */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* Collaborators List */}
        <div className="flex-1 overflow-y-auto p-4">
          <h3 className="text-sm font-medium text-slate-400 mb-3">
            Current Collaborators ({collaborators.length})
          </h3>

          {isLoadingCollaborators ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-slate-400" />
            </div>
          ) : collaborators.length === 0 ? (
            <div className="text-center py-8">
              <UserPlus size={32} className="mx-auto text-slate-600 mb-2" />
              <p className="text-slate-500">No collaborators yet</p>
              {isOwner && (
              <p className="text-sm text-slate-600 mt-1">
                Search for users above to add them
              </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {collaborators.map((collab) => (
                <CollaboratorRow
                  key={collab.user_id}
                  collaborator={collab}
                  listId={listId}
                  isOwner={isOwner}
                  isCurrentUser={collab.user_id === user?.id}
                  onRoleChange={handleRoleChange}
                  onRemove={handleRemove}
                  onLeave={!isOwner ? () => setShowLeaveConfirm(true) : undefined}
                  isUpdating={
                    updateRoleMutation.isPending &&
                    updateRoleMutation.variables?.userId === collab.user_id
                  }
                  isRemoving={
                    removeMutation.isPending &&
                    removeMutation.variables?.userId === collab.user_id
                  }
                />
              ))}
            </div>
          )}

          {/* Leave List Confirmation */}
          {showLeaveConfirm && !isOwner && (
            <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm text-slate-300 mb-3">Are you sure you want to leave this list? You will no longer have access.</p>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setShowLeaveConfirm(false)} className="px-3 py-1.5 text-sm text-slate-400 hover:text-white rounded-lg transition-colors">Cancel</button>
                <button onClick={handleLeaveList} className="px-3 py-1.5 text-sm bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg transition-colors">Leave List</button>
              </div>
            </div>
          )}
        </div>
        </>
        )}

        {/* Footer */}
        <div className="p-4 border-t border-white/[0.06]">
          <button
            onClick={handleClose}
            className="w-full px-4 py-2 bg-[#1A1F26] ring-1 ring-white/[0.08] text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
